import { Toggle } from '@insify/design-system/components';
import { headingColor, headingXxs, headingXxxs, textColor, textSm } from '@insify/design-system/typography';
import { formatMoney } from '@insify/global-money';
import { useAnimatedHeight } from '@insify/react-hooks';
import { TrackedButton, TrackedExpandableContext } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef } from 'react';

import * as styles from './AddOn.module.css';
import { AddOnCollapsible } from './AddOnCollapsible';

import type { AddOnCollapsibleProps } from './AddOnCollapsible';
import type { MoneyField } from '@insify/global-money';
import type { ChangeEventHandler } from 'react';

export type AddOnProps = {
  name: string;
  id: string;
  className?: string;
  icon: React.ReactNode;
  priceAmount?: MoneyField;
  priceDisplay?: React.ReactNode;
  afterPrice?: string;
  title: string;
  description: string;
  footer?: React.ReactNode;
  collapsibleProps: AddOnCollapsibleProps;
  collabsibleAnchor: string;
  included?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export const AddOn = forwardRef<HTMLInputElement, AddOnProps>(
  (
    {
      className,
      name,
      priceAmount,
      priceDisplay,
      icon,
      footer,
      title,
      description,
      collapsibleProps: collapsibleContent,
      onChange,
      included,
      collabsibleAnchor,
      afterPrice,
    },
    toggleRef,
  ) => {
    const { ref: animatedHeightRef, toggle: collapsibleToggle } = useAnimatedHeight<HTMLDivElement>();

    return (
      <div className={clsx(styles.container, className)}>
        <div className={styles.header}>{icon}</div>
        <div className={clsx(styles.content)}>
          <div className={clsx(headingXxxs, headingColor)}>{title}</div>
          <div className={clsx(textSm, textColor)}>{description}</div>
          <div className={styles.contentFooter}>
            <div>
              <TrackedButton
                type='button'
                className={clsx(textSm, textColor, styles.collapsibleAnchor)}
                onClick={collapsibleToggle}
                id={`addOnCollapsibleAnchor-${name}`}
                data-testid={`addOnCollapsibleAnchor-${name}`}
              >
                {collabsibleAnchor}
              </TrackedButton>
            </div>

            <>
              <div className={styles.priceContainer}>
                {priceAmount && !priceDisplay && !included && (
                  <div
                    className={styles.price}
                    id={`addOnPriceAmount-${name}`}
                    data-testid={`addOnPriceAmount-${name}`}
                  >
                    <div className={clsx(headingXxs, headingColor, styles.priceAmount)}>
                      {formatMoney({ cents: priceAmount.cents, currency: priceAmount.currency || 'EUR' }, 'fr')}
                    </div>
                    <span className={clsx(textSm, styles.pricePeriod)}>{afterPrice}</span>
                  </div>
                )}

                {priceDisplay && (
                  <div
                    className={clsx(styles.price, included && clsx(styles.priceIncluded, headingXxs))}
                    id={`addOnPriceDisplay-${name}`}
                    data-testid={`addOnPriceDisplay-${name}`}
                  >
                    {priceDisplay}
                  </div>
                )}

                {onChange && !included && (
                  <div>
                    <Toggle
                      name={name}
                      ref={toggleRef}
                      className={styles.toggle}
                      trackingId={`addOnToggle-${name}`}
                      id={`addOnToggle-${name}`}
                      onChange={onChange}
                      data-testid={`addOnToggle-${name}`}
                    />
                  </div>
                )}
              </div>
            </>
          </div>

          <div className={styles.collapsibleContainer}>
            <div ref={animatedHeightRef}>
              {collapsibleContent && (
                <TrackedExpandableContext
                  objectiv={{
                    id: `addOnCollapsible-${name}`,
                    Component: () => (
                      <div data-testId={`addOnCollapsible-${name}`} className={styles.collapsible}>
                        <AddOnCollapsible {...collapsibleContent} />
                      </div>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    );
  },
);
